import React, { useCallback } from 'react';
import { useAppSelector } from 'store/hooks';
import useColors from 'hooks/useColors';
import { SettingsItem, SettingsItemContent, SettingsItemDescription, SettingsItemTitle } from 'design-system';
import { IconMoneyBill } from 'design-system/icons/IconMoneyBill';
import { IconRocket } from 'design-system/icons/IconRocket';
import Modal from 'utils/packages/Modal';
import Text from 'design-system/Text';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import { blockInvestActionOnWeb } from '../webGate';
import { selectAutoInvestAllocations, selectAutoInvestPlanForAccount, selectTradingAccount, } from '../reducers/selectors';
import { isWeb } from '../../../constants';
const HowToBuyModalContent = ({ assetId, onMarketOrder }) => {
    const colors = useColors();
    const { navigate } = useMainStackNavigation();
    const tradingAccount = useAppSelector(selectTradingAccount);
    const accountId = tradingAccount?.account.id ?? '';
    const accountType = tradingAccount?.account.accountType;
    const plan = useAppSelector((state) => selectAutoInvestPlanForAccount(state, accountId));
    const existingAllocations = useAppSelector(selectAutoInvestAllocations);
    const isPaused = plan?.status === 'paused';
    const isAssetAlreadyAllocated = existingAllocations?.some((allocation) => allocation.assetId === assetId);
    const handleAutoinvest = useCallback(() => {
        if (!accountType)
            return;
        Modal.hide(() => {
            if (blockInvestActionOnWeb('Autoinvest'))
                return;
            if (plan && existingAllocations) {
                navigate('AutoinvestAllocation', {
                    accountType,
                    planId: plan.id,
                    isEditMode: true,
                    isUpsellFlow: true,
                    wasPaused: isPaused,
                    initialAllocations: isAssetAlreadyAllocated
                        ? existingAllocations
                        : [
                            {
                                assetId,
                                percentage: 0,
                            },
                            ...existingAllocations,
                        ],
                });
            }
            else {
                navigate('AutoinvestIntro', {
                    accountType,
                    initialAllocations: [
                        {
                            assetId,
                            percentage: 0,
                        },
                    ],
                });
            }
        });
    }, [accountType, plan, existingAllocations, isPaused, assetId, isAssetAlreadyAllocated]);
    const handleMarketOrder = useCallback(() => {
        Modal.hide(onMarketOrder);
    }, [onMarketOrder]);
    const getSubtitle = useCallback(() => {
        if (plan) {
            return isPaused && isAssetAlreadyAllocated
                ? 'Restart your monthly investments'
                : 'Add this fund to your allocation';
        }
        return 'Invest automatically every month';
    }, [plan, isPaused, isAssetAlreadyAllocated]);
    return (<>
      <SettingsItem iconSize={44} onPress={handleAutoinvest} icon={<IconRocket color={colors.text.investment}/>}>
        <SettingsItemContent>
          <Text Text-12 Primary>
            RECOMMENDED
          </Text>
          <SettingsItemTitle>
            {isPaused && isAssetAlreadyAllocated ? 'Resume Autoinvest' : 'Autoinvest'}
          </SettingsItemTitle>
          <SettingsItemDescription>{getSubtitle()}</SettingsItemDescription>
        </SettingsItemContent>
      </SettingsItem>
      <SettingsItem iconSize={44} title="One-off investment" onPress={handleMarketOrder} icon={<IconMoneyBill gradientKey="darkPurple"/>} description="Your order will be placed at the market price available when executed"/>
    </>);
};
/**
 * NOTE: should only be used in case where trading account id is set
 */
const HowToBuyModal = {
    show: (assetId, onMarketOrder) => {
        Modal.show(<HowToBuyModalContent assetId={assetId} onMarketOrder={onMarketOrder}/>, {
            containerStyle: modalContainerStyle,
            title: 'Choose how to buy',
        });
    },
    hide: () => {
        Modal.hide();
    },
};
const modalContainerStyle = {
    paddingHorizontal: 0,
    overflow: 'hidden',
    // On web, inset the options so their hover highlight sits inside the modal and 8 below the title
    ...(isWeb && { paddingHorizontal: 8, paddingTop: 8, paddingBottom: 8 }),
};
export default HowToBuyModal;
